// src/components/PushNotificationPrompt.js
// Push notification opt-in and management components
// Shows as a card on the Dashboard, toggle on Account page

import React, { useState, useEffect } from 'react';
import { isPushSupported, getPermissionState, subscribeToPush, unsubscribeFromPush, isSubscribedToPush } from '../services/pushNotifications';

/**
 * Dashboard prompt — clean, compact opt-in card
 * Disappears after subscribe or dismiss, remembers via localStorage
 */
export function PushOptInCard({ userId }) {
    const [visible, setVisible] = useState(false);
    const [subscribing, setSubscribing] = useState(false);

    useEffect(() => {
        if (!isPushSupported() || !userId) return;
        const permission = getPermissionState();
        const dismissed = localStorage.getItem('mom-push-dismissed');

        // Show if permission not yet asked and user hasn't dismissed
        if (permission === 'default' && !dismissed) {
            const timer = setTimeout(() => setVisible(true), 3000);
            return () => clearTimeout(timer);
        }
    }, [userId]);

    const handleSubscribe = async () => {
        setSubscribing(true);
        const success = await subscribeToPush(userId);
        setSubscribing(false);
        if (success) {
            setVisible(false);
        } else {
            setVisible(false);
            localStorage.setItem('mom-push-dismissed', '1');
        }
    };

    const handleDismiss = () => {
        setVisible(false);
        localStorage.setItem('mom-push-dismissed', '1');
    };

    if (!visible) return null;

    return (
        <div className="slide-up" style={{
            background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)',
            WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
            borderRadius: 20, border: '1px solid rgba(255,255,255,0.55)',
            padding: '18px 22px', marginBottom: 20,
            boxShadow: 'var(--shadow-md)',
            display: 'flex', alignItems: 'center', gap: 14,
        }}>
            <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600,
                    color: 'var(--mom-charcoal)', marginBottom: 2,
                }}>
                    Stay in the loop
                </p>
                <p style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 13,
                    color: 'var(--mom-warm-gray)', lineHeight: 1.4,
                }}>
                    Get notified when new content is added.
                </p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
                <button onClick={handleDismiss} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)',
                    padding: '8px 4px',
                }}>
                    Not now
                </button>
                <button onClick={handleSubscribe} disabled={subscribing} className="mom-btn mom-btn-primary" style={{
                    padding: '8px 18px', fontSize: 13, borderRadius: 12,
                    opacity: subscribing ? 0.6 : 1,
                }}>
                    {subscribing ? 'Enabling...' : 'Enable'}
                </button>
            </div>
        </div>
    );
}

/**
 * Account page toggle — allows users to manage their notification preference
 */
export function PushNotificationToggle({ userId }) {
    const [supported, setSupported] = useState(false);
    const [subscribed, setSubscribed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [toggling, setToggling] = useState(false);

    useEffect(() => {
        const check = async () => {
            const sup = isPushSupported();
            setSupported(sup);
            if (sup) {
                const sub = await isSubscribedToPush();
                setSubscribed(sub);
            }
            setLoading(false);
        };
        check();
    }, []);

    const handleToggle = async () => {
        setToggling(true);
        if (subscribed) {
            const ok = await unsubscribeFromPush(userId);
            if (ok) setSubscribed(false);
        } else {
            const ok = await subscribeToPush(userId);
            if (ok) {
                setSubscribed(true);
                localStorage.removeItem('mom-push-dismissed');
            }
        }
        setToggling(false);
    };

    if (!supported || loading) return null;

    const permission = getPermissionState();
    const isDenied = permission === 'denied';

    return (
        <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 18px', borderRadius: 14,
            background: 'rgba(237,240,244,0.6)', backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            boxShadow: 'inset 0 2px 4px rgba(0,10,40,0.06), inset 0 1px 1px rgba(0,10,40,0.04), 0 1px 0 rgba(255,255,255,0.60)',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                    width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                    background: subscribed
                        ? 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))'
                        : 'var(--mom-cream-dark)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s',
                }}>
                    <span style={{ fontSize: 18 }}>{subscribed ? '🔔' : '🔕'}</span>
                </div>
                <div>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 2 }}>
                        Push Notifications
                    </p>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)' }}>
                        {isDenied ? 'Blocked by browser — enable in browser settings' : subscribed ? 'Enabled — you\'ll receive updates' : 'Disabled'}
                    </p>
                </div>
            </div>
            {!isDenied && (
                <button onClick={handleToggle} disabled={toggling} className="mom-btn" style={{
                    padding: '8px 18px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                    border: 'none', flexShrink: 0,
                    background: subscribed ? 'rgba(196,121,107,0.1)' : 'var(--mom-primary)',
                    color: subscribed ? 'var(--mom-primary)' : 'var(--mom-white)',
                    opacity: toggling ? 0.5 : 1,
                }}>
                    {toggling ? '...' : subscribed ? 'Turn Off' : 'Turn On'}
                </button>
            )}
        </div>
    );
}